'use client';

import { createClient } from '@/lib/supabase/client';

// ─── Helper ────────────────────────────────────────────────────────────────
function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const cls = error.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error.message) {
    return /relation.*does not exist|column.*does not exist|function.*does not exist|syntax error|type.*does not exist/i.test(error.message);
  }
  return false;
}

// ─── Restaurant ────────────────────────────────────────────────────────────
export async function getOrCreateRestaurant(): Promise<string | null> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('restaurants')
    .select('id')
    .eq('owner_id', user.id)
    .limit(1)
    .maybeSingle();

  if (error && isSchemaError(error)) throw error;
  if (data) return data.id;

  // Create default restaurant for this user
  const { data: created, error: createError } = await supabase
    .from('restaurants')
    .insert({ owner_id: user.id, naam: 'Mijn Restaurant' })
    .select('id')
    .single();

  if (createError) {
    if (isSchemaError(createError)) throw createError;
    return null;
  }
  return created.id;
}

// ─── Categories ────────────────────────────────────────────────────────────
export async function fetchCategories(restaurantId: string) {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .order('volgorde', { ascending: true });

  if (error) {
    if (isSchemaError(error)) throw error;
    return [];
  }
  return (data || []).map((c: any) => ({
    id: c.id,
    naam: c.naam,
    icon: c.icon,
    volgorde: c.volgorde,
    actief: c.actief,
    aantalProducten: 0,
    printerId: c.printer_id,
    restaurantId: c.restaurant_id,
  }));
}

export async function upsertCategory(restaurantId: string, cat: any) {
  const supabase = createClient();
  const payload = {
    restaurant_id: restaurantId,
    naam: cat.naam,
    icon: cat.icon,
    volgorde: cat.volgorde,
    actief: cat.actief,
    printer_id: cat.printerId ?? null,
  };
  if (cat.id && !cat.id.startsWith('cat-')) {
    const { data, error } = await supabase.from('categories').update(payload).eq('id', cat.id).select().single();
    if (error && isSchemaError(error)) throw error;
    return data;
  }
  const { data, error } = await supabase.from('categories').insert(payload).select().single();
  if (error && isSchemaError(error)) throw error;
  return data;
}

export async function deleteCategory(id: string) {
  const supabase = createClient();
  const { error } = await supabase.from('categories').delete().eq('id', id);
  if (error && isSchemaError(error)) throw error;
}

// ─── Products ──────────────────────────────────────────────────────────────
export async function fetchProducts(restaurantId: string) {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .order('created_at', { ascending: true });

  if (error) {
    if (isSchemaError(error)) throw error;
    return [];
  }
  return (data || []).map((p: any) => ({
    id: p.id,
    naam: p.naam,
    beschrijving: p.beschrijving,
    prijs: Number(p.prijs),
    btw: p.btw,
    categorieId: p.category_id,
    allergenen: p.allergenen || [],
    actief: p.actief,
    populair: p.populair,
    aanbevolen: p.aanbevolen,
    foto: p.foto,
    beschikbaarVan: p.beschikbaar_van,
    beschikbaarTot: p.beschikbaar_tot,
    opties: p.opties || [],
    barcode: p.barcode,
    calorieën: p.calorieen,
    printDestinaties: p.print_destinaties || [],
    restaurantId: p.restaurant_id,
  }));
}

export async function upsertProduct(restaurantId: string, product: any) {
  const supabase = createClient();
  const payload = {
    restaurant_id: restaurantId,
    category_id: product.categorieId ?? null,
    naam: product.naam,
    beschrijving: product.beschrijving ?? '',
    prijs: product.prijs,
    btw: product.btw,
    allergenen: product.allergenen || [],
    actief: product.actief,
    populair: product.populair,
    aanbevolen: product.aanbevolen,
    foto: product.foto ?? null,
    beschikbaar_van: product.beschikbaarVan ?? null,
    beschikbaar_tot: product.beschikbaarTot ?? null,
    opties: product.opties || [],
    barcode: product.barcode ?? null,
    calorieen: product.calorieën ?? null,
    print_destinaties: product.printDestinaties || [],
  };
  // If id is a UUID (not a mock id like 'mp-001'), update
  const isUUID = product.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(product.id);
  if (isUUID) {
    const { data, error } = await supabase.from('products').update(payload).eq('id', product.id).select().single();
    if (error && isSchemaError(error)) throw error;
    return data;
  }
  const { data, error } = await supabase.from('products').insert(payload).select().single();
  if (error && isSchemaError(error)) throw error;
  return data;
}

export async function deleteProduct(id: string) {
  const supabase = createClient();
  const { error } = await supabase.from('products').delete().eq('id', id);
  if (error && isSchemaError(error)) throw error;
}

// ─── Restaurant Tables ─────────────────────────────────────────────────────
export async function fetchRestaurantTables(restaurantId: string) {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('restaurant_tables')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .order('nummer', { ascending: true });

  if (error) {
    if (isSchemaError(error)) throw error;
    return [];
  }
  return (data || []).map((t: any) => ({
    id: t.id,
    nummer: t.nummer,
    zone: t.zone,
    status: t.status,
    couverts: t.couverts,
    medewerker: t.medewerker ?? undefined,
    geopend: t.geopend ?? undefined,
    orderItems: t.order_items || [],
    totalAmount: Number(t.total_amount) || 0,
    notitie: t.notitie ?? undefined,
    serviceType: t.service_type ?? 'a_la_carte',
    gastenAantal: t.gasten_aantal ?? 0,
    pausedAt: t.paused_at ?? undefined,
  }));
}

export async function upsertRestaurantTable(restaurantId: string, table: any) {
  const supabase = createClient();
  const payload = {
    restaurant_id: restaurantId,
    nummer: table.nummer,
    zone: table.zone,
    status: table.status,
    couverts: table.couverts,
    medewerker: table.medewerker ?? null,
    geopend: table.geopend ?? null,
    order_items: table.orderItems || [],
    total_amount: table.totalAmount || 0,
    notitie: table.notitie ?? null,
  };
  const isUUID = table.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(table.id);
  if (isUUID) {
    const { data, error } = await supabase.from('restaurant_tables').update(payload).eq('id', table.id).select().single();
    if (error && isSchemaError(error)) throw error;
    return data;
  }
  const { data, error } = await supabase.from('restaurant_tables').insert(payload).select().single();
  if (error && isSchemaError(error)) throw error;
  return data;
}

export async function updateTableStatus(tableId: string, updates: Partial<any>) {
  const supabase = createClient();
  const payload: any = {};
  if (updates.status !== undefined) payload.status = updates.status;
  if (updates.orderItems !== undefined) payload.order_items = updates.orderItems;
  if (updates.totalAmount !== undefined) payload.total_amount = updates.totalAmount;
  if (updates.medewerker !== undefined) payload.medewerker = updates.medewerker;
  if (updates.geopend !== undefined) payload.geopend = updates.geopend;
  if (updates.couverts !== undefined) payload.couverts = updates.couverts;
  if (updates.notitie !== undefined) payload.notitie = updates.notitie;

  const { error } = await supabase.from('restaurant_tables').update(payload).eq('id', tableId);
  if (error && isSchemaError(error)) throw error;
}

// ─── Staff ─────────────────────────────────────────────────────────────────
export async function fetchStaff(restaurantId: string) {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('staff_members')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .order('created_at', { ascending: true });

  if (error) {
    if (isSchemaError(error)) throw error;
    return [];
  }
  return (data || []).map((s: any) => ({
    id: s.id,
    name: s.naam,
    role: s.role,
    pin: s.pin,
    avatar: s.avatar,
    color: s.color,
    active: s.actief,
    screenPermissions: s.screen_permissions ?? null,
  }));
}

export async function upsertStaffMember(restaurantId: string, member: any) {
  const supabase = createClient();
  const payload = {
    restaurant_id: restaurantId,
    naam: member.name,
    role: member.role,
    pin: member.pin,
    avatar: member.avatar,
    color: member.color,
    actief: member.active,
    screen_permissions: member.screenPermissions ?? null,
  };
  const isUUID = member.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(member.id);
  if (isUUID) {
    const { data, error } = await supabase.from('staff_members').update(payload).eq('id', member.id).select().single();
    if (error) throw error;
    return data;
  }
  const { data, error } = await supabase.from('staff_members').insert(payload).select().single();
  if (error) throw error;
  return data;
}

export async function deleteStaffMember(id: string) {
  const supabase = createClient();
  const { error } = await supabase.from('staff_members').delete().eq('id', id);
  if (error && isSchemaError(error)) throw error;
}

// ─── Print Devices ─────────────────────────────────────────────────────────
export async function fetchPrintDevices(restaurantId: string) {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('print_devices')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .order('created_at', { ascending: true });

  if (error) {
    if (isSchemaError(error)) throw error;
    return [];
  }
  return (data || []).map((d: any) => ({
    id: d.id,
    naam: d.naam,
    type: d.type,
    ipAdres: d.ip_adres,
    poort: d.poort,
    locatie: d.locatie,
    model: d.model,
    actief: d.actief,
    status: 'unknown' as const,
  }));
}

export async function upsertPrintDevice(restaurantId: string, device: any) {
  const supabase = createClient();
  const payload = {
    restaurant_id: restaurantId,
    naam: device.naam,
    type: device.type,
    ip_adres: device.ipAdres,
    poort: device.poort,
    locatie: device.locatie ?? null,
    model: device.model ?? null,
    actief: device.actief,
  };
  const isUUID = device.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(device.id);
  if (isUUID) {
    const { data, error } = await supabase.from('print_devices').update(payload).eq('id', device.id).select().single();
    if (error && isSchemaError(error)) throw error;
    return data;
  }
  const { data, error } = await supabase.from('print_devices').insert(payload).select().single();
  if (error && isSchemaError(error)) throw error;
  return data;
}

export async function deletePrintDevice(id: string) {
  const supabase = createClient();
  const { error } = await supabase.from('print_devices').delete().eq('id', id);
  if (error && isSchemaError(error)) throw error;
}

// ─── Floor Plan ────────────────────────────────────────────────────────────
export async function fetchFloorPlan(restaurantId: string) {
  const supabase = createClient();
  const [settingsRes, tablesRes, decorativesRes] = await Promise.all([
    supabase.from('floor_plan_settings').select('*').eq('restaurant_id', restaurantId).maybeSingle(),
    supabase.from('floor_plan_tables').select('*').eq('restaurant_id', restaurantId),
    supabase.from('floor_plan_decoratives').select('*').eq('restaurant_id', restaurantId),
  ]);

  const background = settingsRes.data?.background ?? 'wood';
  const tables = (tablesRes.data || []).map((t: any) => ({
    id: t.table_ref_id,
    x: Number(t.x),
    y: Number(t.y),
    shape: t.shape,
    label: t.label,
    couverts: t.couverts,
    zone: t.zone,
  }));
  const decoratives = (decorativesRes.data || []).map((d: any) => ({
    id: d.deco_ref_id,
    type: d.type,
    x: Number(d.x),
    y: Number(d.y),
    width: Number(d.width),
    height: Number(d.height),
    label: d.label,
    rotation: Number(d.rotation),
  }));

  return { background, tables, decoratives };
}

export async function saveFloorPlan(restaurantId: string, layout: any) {
  const supabase = createClient();

  // Upsert settings
  await supabase.from('floor_plan_settings').upsert(
    { restaurant_id: restaurantId, background: layout.background },
    { onConflict: 'restaurant_id' }
  );

  // Replace tables
  await supabase.from('floor_plan_tables').delete().eq('restaurant_id', restaurantId);
  if (layout.tables?.length > 0) {
    await supabase.from('floor_plan_tables').insert(
      layout.tables.map((t: any) => ({
        restaurant_id: restaurantId,
        table_ref_id: t.id,
        x: t.x,
        y: t.y,
        shape: t.shape,
        label: t.label,
        couverts: t.couverts,
        zone: t.zone,
      }))
    );
  }

  // Replace decoratives
  await supabase.from('floor_plan_decoratives').delete().eq('restaurant_id', restaurantId);
  if (layout.decoratives?.length > 0) {
    await supabase.from('floor_plan_decoratives').insert(
      layout.decoratives.map((d: any) => ({
        restaurant_id: restaurantId,
        deco_ref_id: d.id,
        type: d.type,
        x: d.x,
        y: d.y,
        width: d.width,
        height: d.height,
        label: d.label ?? null,
        rotation: d.rotation ?? 0,
      }))
    );
  }
}

// ─── Restaurant Profile ────────────────────────────────────────────────────
export async function fetchRestaurantProfile(restaurantId: string) {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('restaurants')
    .select('*')
    .eq('id', restaurantId)
    .single();
  if (error && isSchemaError(error)) throw error;
  if (!data) return null;
  return {
    id: data.id,
    naam: data.naam,
    adres: data.adres ?? '',
    telefoon: data.telefoon ?? '',
    email: data.email ?? '',
    kvk: data.kvk ?? '',
    btwNummer: data.btw_nummer ?? '',
    logoUrl: data.logo_url ?? '',
    actief: data.actief,
  };
}

export async function updateRestaurantProfile(restaurantId: string, profile: any) {
  const supabase = createClient();
  const payload: any = {};
  if (profile.naam !== undefined) payload.naam = profile.naam;
  if (profile.adres !== undefined) payload.adres = profile.adres;
  if (profile.telefoon !== undefined) payload.telefoon = profile.telefoon;
  if (profile.email !== undefined) payload.email = profile.email;
  if (profile.kvk !== undefined) payload.kvk = profile.kvk;
  if (profile.btwNummer !== undefined) payload.btw_nummer = profile.btwNummer;
  if (profile.logoUrl !== undefined) payload.logo_url = profile.logoUrl;
  const { data, error } = await supabase.from('restaurants').update(payload).eq('id', restaurantId).select().single();
  if (error && isSchemaError(error)) throw error;
  return data;
}

// ─── Zones ─────────────────────────────────────────────────────────────────
export async function fetchZones(restaurantId: string) {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('zones')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .order('volgorde', { ascending: true });
  if (error) {
    if (isSchemaError(error)) throw error;
    return [];
  }
  return (data || []).map((z: any) => ({
    id: z.id,
    naam: z.naam,
    kleur: z.kleur,
    actief: z.actief,
    volgorde: z.volgorde,
  }));
}

export async function upsertZone(restaurantId: string, zone: any) {
  const supabase = createClient();
  const payload = {
    restaurant_id: restaurantId,
    naam: zone.naam,
    kleur: zone.kleur ?? '#7C3AED',
    actief: zone.actief ?? true,
    volgorde: zone.volgorde ?? 0,
  };
  const isUUID = zone.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(zone.id);
  if (isUUID) {
    const { data, error } = await supabase.from('zones').update(payload).eq('id', zone.id).select().single();
    if (error && isSchemaError(error)) throw error;
    return data;
  }
  const { data, error } = await supabase.from('zones').insert(payload).select().single();
  if (error && isSchemaError(error)) throw error;
  return data;
}

export async function deleteZone(id: string) {
  const supabase = createClient();
  const { error } = await supabase.from('zones').delete().eq('id', id);
  if (error && isSchemaError(error)) throw error;
}

// ─── Staff Roles ───────────────────────────────────────────────────────────
export async function fetchStaffRoles(restaurantId: string) {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('staff_roles')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .order('created_at', { ascending: true });
  if (error) {
    if (isSchemaError(error)) throw error;
    return [];
  }
  return (data || []).map((r: any) => ({
    id: r.id,
    naam: r.naam,
    slug: r.slug,
    rechten: r.rechten || [],
    kleur: r.kleur,
    actief: r.actief,
  }));
}

export async function upsertStaffRole(restaurantId: string, role: any) {
  const supabase = createClient();
  const payload = {
    restaurant_id: restaurantId,
    naam: role.naam,
    slug: role.slug ?? role.naam.toLowerCase().replace(/\s+/g, '_'),
    rechten: role.rechten || [],
    kleur: role.kleur ?? '#0891B2',
    actief: role.actief ?? true,
  };
  const isUUID = role.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(role.id);
  if (isUUID) {
    const { data, error } = await supabase.from('staff_roles').update(payload).eq('id', role.id).select().single();
    if (error && isSchemaError(error)) throw error;
    return data;
  }
  const { data, error } = await supabase.from('staff_roles').insert(payload).select().single();
  if (error && isSchemaError(error)) throw error;
  return data;
}

export async function deleteStaffRole(id: string) {
  const supabase = createClient();
  const { error } = await supabase.from('staff_roles').delete().eq('id', id);
  if (error && isSchemaError(error)) throw error;
}

// ─── Product Translations ──────────────────────────────────────────────────
export async function fetchProductTranslations(restaurantId: string, languageCode?: string) {
  const supabase = createClient();
  let query = supabase
    .from('products_translations')
    .select('*')
    .eq('restaurant_id', restaurantId);

  if (languageCode) {
    query = query.eq('language_code', languageCode);
  }

  const { data, error } = await query;
  if (error) {
    if (isSchemaError(error)) throw error;
    return [];
  }
  return (data || []).map((t: any) => ({
    id: t.id,
    restaurantId: t.restaurant_id,
    productId: t.product_id,
    languageCode: t.language_code,
    naam: t.naam,
    beschrijving: t.beschrijving,
  }));
}

export async function upsertProductTranslations(
  restaurantId: string,
  translations: Array<{ productId: string; languageCode: string; naam: string; beschrijving: string }>
) {
  const supabase = createClient();
  const rows = translations.map((tr) => ({
    restaurant_id: restaurantId,
    product_id: tr.productId,
    language_code: tr.languageCode,
    naam: tr.naam,
    beschrijving: tr.beschrijving,
  }));

  const { error } = await supabase
    .from('products_translations')
    .upsert(rows, { onConflict: 'product_id,language_code' });

  if (error) {
    if (isSchemaError(error)) throw error;
    throw error;
  }
}

export async function deleteProductTranslationsByLanguage(restaurantId: string, languageCode: string) {
  const supabase = createClient();
  const { error } = await supabase
    .from('products_translations')
    .delete()
    .eq('restaurant_id', restaurantId)
    .eq('language_code', languageCode);

  if (error && isSchemaError(error)) throw error;
}

// ─── KDS Orders (Real-time Kitchen/Bar Display) ────────────────────────────

export interface KdsOrderItem {
  id: string;
  naam: string;
  aantal: number;
  optie?: string;
  notitie?: string;
  status: 'pending' | 'ready';
  categorie: string;
}

export interface KdsOrder {
  id: string;
  restaurantId: string;
  bonNummer: string;
  tafelNummer: number;
  zone: string;
  couverts: number;
  medewerker: string;
  notitie?: string;
  items: KdsOrderItem[];
  displayType: 'kitchen' | 'bar';
  status: 'new' | 'in_progress' | 'ready';
  completedAt?: string;
  createdAt: number;
}

function mapKdsOrder(row: any): KdsOrder {
  return {
    id: row.id,
    restaurantId: row.restaurant_id,
    bonNummer: row.bon_nummer,
    tafelNummer: row.tafel_nummer,
    zone: row.zone,
    couverts: row.couverts,
    medewerker: row.medewerker,
    notitie: row.notitie ?? undefined,
    items: row.items || [],
    displayType: row.display_type,
    status: row.status,
    completedAt: row.completed_at ?? undefined,
    createdAt: new Date(row.created_at).getTime(),
  };
}

export async function fetchKdsOrders(restaurantId: string, displayType: 'kitchen' | 'bar'): Promise<KdsOrder[]> {
  const supabase = createClient();
  const since = new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(); // last 4 hours
  const { data, error } = await supabase
    .from('kds_orders')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .eq('display_type', displayType)
    .gte('created_at', since)
    .order('created_at', { ascending: true });

  if (error) {
    if (isSchemaError(error)) throw error;
    return [];
  }
  return (data || []).map(mapKdsOrder);
}

export async function insertKdsOrder(restaurantId: string, order: Omit<KdsOrder, 'id' | 'restaurantId' | 'createdAt'>): Promise<KdsOrder | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('kds_orders')
    .insert({
      restaurant_id: restaurantId,
      bon_nummer: order.bonNummer,
      tafel_nummer: order.tafelNummer,
      zone: order.zone,
      couverts: order.couverts,
      medewerker: order.medewerker,
      notitie: order.notitie ?? null,
      items: order.items,
      display_type: order.displayType,
      status: order.status,
    })
    .select()
    .single();

  if (error) {
    if (isSchemaError(error)) throw error;
    return null;
  }
  return mapKdsOrder(data);
}

export async function updateKdsOrderStatus(
  orderId: string,
  status: 'new' | 'in_progress' | 'ready',
  items?: KdsOrderItem[]
): Promise<void> {
  const supabase = createClient();
  const payload: any = { status };
  if (items !== undefined) payload.items = items;
  if (status === 'ready') payload.completed_at = new Date().toISOString();

  const { error } = await supabase
    .from('kds_orders')
    .update(payload)
    .eq('id', orderId);

  if (error && isSchemaError(error)) throw error;
}

export function subscribeKdsOrders(
  restaurantId: string,
  displayType: 'kitchen' | 'bar',
  onUpdate: (orders: KdsOrder[]) => void
) {
  const supabase = createClient();

  const channel = supabase
    .channel(`kds_orders_${restaurantId}_${displayType}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'kds_orders',
        filter: `restaurant_id=eq.${restaurantId}`,
      },
      async () => {
        // Re-fetch on any change
        const orders = await fetchKdsOrders(restaurantId, displayType);
        onUpdate(orders);
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

// ─── POS Orders (Completed/Paid) ───────────────────────────────────────────

export async function insertPosOrder(restaurantId: string, order: {
  bonNummer: string;
  tafelNummer: number;
  zone: string;
  medewerker: string;
  items: any[];
  subtotaal: number;
  btwBedrag: number;
  totaal: number;
  betaalmethode: string;
  notitie?: string;
}): Promise<string | null> {
  const supabase = createClient();

  // Map betaalmethode to valid enum values
  const methodMap: Record<string, string> = {
    pin: 'PIN',
    contant: 'Contant',
    ideal: 'iDEAL QR',
    tikkie: 'PIN', // fallback
    rekening: 'Rekening',
    combinatie: 'PIN', // fallback
    creditcard: 'Creditcard',
  };
  const betaalmethode = methodMap[order.betaalmethode.toLowerCase()] ?? 'PIN';

  const { data, error } = await supabase
    .from('orders')
    .insert({
      restaurant_id: restaurantId,
      bon_nummer: order.bonNummer,
      tafel_nummer: order.tafelNummer,
      zone: order.zone,
      medewerker: order.medewerker,
      items: order.items,
      subtotaal: order.subtotaal,
      btw_bedrag: order.btwBedrag,
      totaal: order.totaal,
      betaalmethode: betaalmethode,
      status: 'betaald',
      notitie: order.notitie ?? '',
    })
    .select('id')
    .single();

  if (error) {
    if (isSchemaError(error)) throw error;
    return null;
  }
  return data?.id ?? null;
}

// ─── Split Bills ───────────────────────────────────────────────────────────

export async function insertSplitBill(restaurantId: string, bill: {
  bonNummer: string;
  tafelNummer: number;
  splitMode: string;
  splitCount: number;
  items: any[];
  subtotaal: number;
  kortingPct: number;
  kortingBedrag: number;
  totaal: number;
  betaalmethode: string;
}): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('split_bills')
    .insert({
      restaurant_id: restaurantId,
      bon_nummer: bill.bonNummer,
      tafel_nummer: bill.tafelNummer,
      split_mode: bill.splitMode,
      split_count: bill.splitCount,
      items: bill.items,
      subtotaal: bill.subtotaal,
      korting_pct: bill.kortingPct,
      korting_bedrag: bill.kortingBedrag,
      totaal: bill.totaal,
      betaalmethode: bill.betaalmethode,
      status: 'paid',
    });

  if (error && isSchemaError(error)) throw error;
}
